import React, { useState, useRef, useEffect } from 'react';

interface TooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
}

export const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  position = 'bottom'
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [actualPosition, setActualPosition] = useState(position);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isVisible && tooltipRef.current && containerRef.current) {
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let newPosition = position;

      // Debug tooltip positioning
      console.log('Tooltip positioning:', {
        originalPosition: position,
        tooltipHeight: tooltipRect.height,
        tooltipWidth: tooltipRect.width,
        containerTop: containerRect.top,
        containerBottom: containerRect.bottom,
        containerLeft: containerRect.left,
        containerRight: containerRect.right,
        viewportHeight,
        viewportWidth,
        spaceAbove: containerRect.top,
        spaceBelow: viewportHeight - containerRect.bottom,
        spaceLeft: containerRect.left,
        spaceRight: viewportWidth - containerRect.right
      });

      // Check if tooltip would go off-screen and flip position if needed
      if (position === 'top' && containerRect.top - tooltipRect.height < 10) {
        newPosition = 'bottom';
      } else if (position === 'bottom' && containerRect.bottom + tooltipRect.height > viewportHeight - 10) {
        newPosition = 'top';
      } else if (position === 'left' && containerRect.left - tooltipRect.width < 10) {
        newPosition = 'right';
      } else if (position === 'right' && containerRect.right + tooltipRect.width > viewportWidth - 10) {
        newPosition = 'left';
      }

      // Second pass: if still off-screen after flip, try other positions
      if (newPosition === 'top' && containerRect.top - tooltipRect.height < 10) {
        if (containerRect.left > viewportWidth / 2) {
          newPosition = 'left';
        } else {
          newPosition = 'right';
        }
      } else if (newPosition === 'bottom' && containerRect.bottom + tooltipRect.height > viewportHeight - 10) {
        if (containerRect.left > viewportWidth / 2) {
          newPosition = 'left';
        } else {
          newPosition = 'right';
        }
      }

      setActualPosition(newPosition);
    }
  }, [isVisible, position]);

  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2'
  };

  return (
    <div className="relative inline-block" ref={containerRef}>
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => {
          setIsVisible(false);
          setActualPosition(position); // Reset to original position when hidden
        }}
      >
        {children}
      </div>
      {isVisible && (
        <div
          ref={tooltipRef}
          className={`absolute z-[9999] ${positionClasses[actualPosition]}
                     w-96 p-3 bg-white border border-gray-200 rounded-lg shadow-xl
                     pointer-events-none`}
          style={{
            maxHeight: '80vh',
            overflowY: 'auto'
          }}
        >
          <div className="relative">
            {/* Arrow */}
            <div className={`absolute w-3 h-3 bg-white border-gray-200 rotate-45
              ${actualPosition === 'top' ? 'top-full left-1/2 -translate-x-1/2 -mt-1.5 border-r border-b' :
                actualPosition === 'bottom' ? 'bottom-full left-1/2 -translate-x-1/2 -mb-1.5 border-l border-t' :
                actualPosition === 'left' ? 'left-full top-1/2 -translate-y-1/2 -ml-1.5 border-t border-r' :
                'right-full top-1/2 -translate-y-1/2 -mr-1.5 border-b border-l'
              }`}
            />
            {content}
          </div>
        </div>
      )}
    </div>
  );
};